import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import QRCode from 'qrcode';
import { toast } from 'react-toastify';
import whatsappMicroserviceService from '../../../services/whatsappMicroserviceService';

function WhatsAppQRModal({ open, onClose, onConnected }) {
  const [loading, setLoading] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [message, setMessage] = useState('Esperando QR...');
  const isFetchingRef = useRef(false);
  const reconnectRequestedRef = useRef(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const fetchQr = async () => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;

      try {
        const health = await whatsappMicroserviceService.getHealth();
        const status = health?.status || health?.whatsapp?.status;
        const isConnected = status === 'connected' || health?.connected === true;

        if (status === 'reconnect_paused' && !reconnectRequestedRef.current) {
          reconnectRequestedRef.current = true;

          try {
            await whatsappMicroserviceService.reconnect();
            if (!cancelled) {
              setMessage('Intentando reconectar WhatsApp...');
            }
          } catch (_error) {
            if (!cancelled) {
              setMessage('No se pudo iniciar reconexion. Intenta cerrar y abrir el modal.');
            }
          }
        }

        if (isConnected) {
          reconnectRequestedRef.current = false;
          if (!cancelled) {
            onConnected();
          }
          return;
        }

        if (!cancelled) setLoading(true);
        const qrResponse = await whatsappMicroserviceService.getQr();
        const qrText = qrResponse?.qr;

        if (qrText) {
          const url = await QRCode.toDataURL(qrText, { margin: 1, width: 260 });
          if (!cancelled) {
            setQrDataUrl(url);
            setMessage('Escanea el QR con WhatsApp para vincular la cuenta.');
          }
        } else if (!cancelled) {
          setQrDataUrl('');
          if (qrResponse?.status === 'reconnect_paused') {
            setMessage('WhatsApp rechazo el registro (405). Se requiere reconexion manual y/o limpiar sesion.');
          } else {
            setMessage(qrResponse?.message || 'Aun no hay QR disponible. Reintentando...');
          }
        }
      } catch (_error) {
        if (!cancelled) {
          setMessage('No se pudo obtener el QR. Reintentando...');
        }
      } finally {
        isFetchingRef.current = false;
        if (!cancelled) setLoading(false);
      }
    };

    fetchQr();
    const interval = setInterval(fetchQr, 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
      isFetchingRef.current = false;
    };
  }, [open, onConnected]);

  useEffect(() => {
    if (!open) {
      reconnectRequestedRef.current = false;
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="wa-modal-overlay" role="dialog" aria-modal="true" aria-label="QR de WhatsApp">
      <div className="wa-modal-content">
        <div className="wa-modal-header">
          <h4>Vincular WhatsApp</h4>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>

        <div className="wa-modal-body">
          {loading && !qrDataUrl && (
            <div className="wa-loader-block">
              <div className="spinner-border text-success" role="status" />
              <p>Cargando QR...</p>
            </div>
          )}

          {!loading && qrDataUrl && (
            <img src={qrDataUrl} alt="QR WhatsApp" className="wa-qr-image" />
          )}

          <p className="wa-modal-help">{message}</p>
        </div>
      </div>
    </div>
  );
}

WhatsAppQRModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConnected: PropTypes.func
};

WhatsAppQRModal.defaultProps = {
  onConnected: () => {
    toast.success('WhatsApp conectado correctamente.');
  }
};

export default WhatsAppQRModal;
